import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Container from '@material-ui/core/Container';
import Grid from '@material-ui/core/Grid';
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import Carousel from '@hydra/common/components/Carousel';

import Translate from '@hydra/common/components/i18n';
import WistiaPlayer from '@hydra/common/components/WistiaPlayer';

const useStyles = makeStyles((theme) => ({
  wrapper: {
    width: '100%',
    marginBottom: theme.spacing(12.5)
  },
  videoContainer: {
    paddingBottom: theme.spacing(3),
    marginBottom: theme.spacing(3),
    [theme.breakpoints.up('sm')]: {
      paddingBottom: theme.spacing(4),
      marginBottom: theme.spacing(4)
    }
  },
  slideTitle: {
    fontSize: theme.spacing(3.5),
    fontWeight: theme.typography.fontWeightSemibold,
    padding: theme.spacing(2)
  },
  descriptionPadding: { padding: theme.spacing(3, 2, 2) },
  rounded: {
    borderRadius: theme.shape.borderRadius * 4
  },
  heading: {
    fontSize: theme.typography.fontSizeXLarge,
    fontWeight: theme.typography.fontWeightSemibold,
    lineHeight: 1.4,
    marginBottom: theme.spacing(4.5)
  }
}));

const videoIds = {
  optimizeYourWebsite: '1woeadrufy',
  sellYourServices: 'lcoqvgcj9d',
  analyzeTheCompetition: 'thhl68huu3',
  designWhiteLabelReports: 's4hjiwx9ek',
  automateDataCollection: 'yp6i2uezl4'
};

const slidesArray = [
  {
    title: 'free_review_tab_one_heading',
    description: 'free_review_tab_one_description',
    videoId: videoIds.optimizeYourWebsite
  },
  {
    title: 'free_review_tab_two_heading',
    description: 'free_review_tab_two_description',
    videoId: videoIds.sellYourServices
  },
  {
    title: 'free_review_tab_three_heading',
    description: 'free_review_tab_three_description',
    videoId: videoIds.analyzeTheCompetition
  },
  {
    title: 'free_review_tab_four_heading',
    description: 'free_review_tab_four_description',
    videoId: videoIds.designWhiteLabelReports
  },
  {
    title: 'free_review_tab_five_heading',
    description: 'free_review_tab_five_description',
    videoId: videoIds.automateDataCollection
  }
];

function a11yProps (index: number) {
  return {
    id: `carousel-item-${index}`,
    'aria-controls': `carousel-item-${index}`
  };
}

interface Slide {
  title: string
  description: string
  videoId: string
}

function VideoSlide ({ slide }: { slide: Slide }) {
  const classes = useStyles();
  const { videoId, title, description } = slide;
  return (
    <Grid container>
      <Grid item xs={12} className={classes.videoContainer}>
        <WistiaPlayer hashedId={videoId} muted />
      </Grid>
      <Paper className={classes.rounded}>
        <Grid container>
          <Grid item xs={12} sm={6}>
            <Typography color='primary' className={classes.slideTitle}>
              <Translate component='span' content={title} />
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography className={classes.descriptionPadding}>
              <Translate component='span' content={description} />
            </Typography>
          </Grid>
        </Grid>
      </Paper>
    </Grid>
  );
}

export default function ReviewTeaserCarousel () {
  const classes = useStyles();
  return (
    <Container
      maxWidth='lg'
      className={classes.wrapper}
    >
      <Typography
        align='center'
        component='h2'
        variant='h2'
        color='textPrimary'
        className={classes.heading}
      >
        <Translate content='free_review_carousel_title' />
      </Typography>
      <Carousel
        fullWidth
        showPagination
        slides={slidesArray.map((value, key) => <VideoSlide key={key} slide={value} {...a11yProps(key)} />)}
        arrowsProps={{
          size: 'lg',
          showInactiveArrow: false,
          prevEvent: 'review-loading-tips-previous-arrow',
          nextEvent: 'review-loading-tips-next-arrow'
        }}
      />
    </Container>
  );
}
